/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	OnDestroy,
	ViewChild,
	ElementRef,
	AfterViewInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';
import { Subject, BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil, map, filter, switchMap, distinctUntilChanged, tap } from 'rxjs/operators';
import { clipboardManager, TicketCopierOperation } from 'src/app/plain-javascript/services/ClipboardManager';
import { DragAndDropHandler, AbortOrContinue } from 'src/app/plain-javascript/services/DragAndDropHandler';
import { PasteMode } from './PasteMode';
import * as strings from '@touchplan/strings';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { PopupService } from 'src/app/common/popup/popup.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

import { dateCache } from '@touchplan/common';
import { SimplePopupInputs, PopupOutputType } from 'src/app/common/popup/interfaces';
import { MoveActiveLinePopupComponent } from './move-active-line-popup/move-active-line-popup.component';
import { PasteModesInfoPopupComponent } from './paste-modes-info-popup/paste-modes-info-popup.component';
import { PlanUndosService } from '../plan-undos.service';
import { UntypedFormControl } from '@angular/forms';

interface ISelectItem {
	label: string;
	value: PasteMode;
	icon: string;
}

const IMAGE_DIR = 'assets/images/';

@Component({
	selector: 'clipboard-in-dock',
	templateUrl: './clipboard-in-dock.component.html',
	styleUrls: ['./clipboard-in-dock.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * The component for handling the Clipboard UI in the floating tickets dock.
 */
export class ClipboardInDockComponent implements OnDestroy, AfterViewInit {
	private static IMAGE_DIR = IMAGE_DIR;
	private static CLIPBOARD_IMAGE_EMPTY = IMAGE_DIR + 'paste-from-cb-white-semi-opaque.png';
	private static CLIPBOARD_IMAGE_WITH_TICKETS = IMAGE_DIR + 'paste-from-clipboard-blue.png';
	private static CLIPBOARD_IMAGE_DISABLED = IMAGE_DIR + 'paste-from-clipboard-disabled.png';

	@ViewChild('clipboardDragAndDropElement', { static: false }) public clipboardDragAndDropElement: ElementRef;

	// TODO These can probably be replaced with template variables at some point
	public showClearButton$ = new BehaviorSubject(this.showClearButton());
	public clipboardHasPullTickets$ = new BehaviorSubject(clipboardManager.ticketsArePullSide());
	public showWarningIcon$ = clipboardManager.showWarningIcon$;
	public ticketAttachmentsCopyInProgress$ = clipboardManager.ticketAttachmentsCopyInProgress$;

	public isClipboardEnabled$: Observable<boolean>;
	public clipboardLabel = strings.CLIPBOARD_LABEL;
	public ticketCountString = '';
	public imageSource = ClipboardInDockComponent.CLIPBOARD_IMAGE_EMPTY;

	public dropdownOptions: ISelectItem[] = [
		{ label: strings.PASTE_MODE_EXACT_COPY, value: PasteMode.ExactCopy, icon: '' },
		{ label: strings.PASTE_MODE_TEMPLATE_CURRENT, value: PasteMode.TemplateCurrent, icon: '' },
		{ label: strings.PASTE_MODE_TEMPLATE_ORIGINAL, value: PasteMode.TemplateOriginal, icon: '' },
	];

	private readOnlyAccess = false;
	private dragAndDropHandler: DragAndDropHandler;
	private shutdownSubject$ = new Subject<void>();

	public selectedModeControl: UntypedFormControl = new UntypedFormControl(PasteMode.ExactCopy);

	constructor(
		private planState: PlanState,
		private planAccessCheck: PlanAccessCheck,
		private popupService: PopupService,
		private planUndosService: PlanUndosService,
		private featureFlagManager: FeatureFlagManager
	) {
		this.selectedModeControl.setValue(PasteMode.ExactCopy);

		clipboardManager.planState = planState;
		clipboardManager.planAccessCheck = planAccessCheck;
		clipboardManager.planUndosService = planUndosService;
		clipboardManager.featureFlagManager = featureFlagManager;

		this.isClipboardEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.CLIPBOARD);

		// Show Active line move confirmation dialog on the "problematic Paste All condition",
		// when Paste All is the paste mode and the Active line isn't out far enough:
		clipboardManager.operation$
			.pipe(
				filter((op) => op === TicketCopierOperation.PasteAbortOnActiveLineCross),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((op) => this.displayActiveLineMovePrompt());

		// Update value in clipboard dropdown and set Template - Original
		// when 'Copy to clipboard' button is pressed from Multi select menu
		clipboardManager.operation$
			.pipe(
				filter((op) => op === TicketCopierOperation.CopyTickets),
				// Update only in case of new and defined value
				filter(
					() =>
						clipboardManager.currentPasteMode &&
						clipboardManager.currentPasteMode !== this.selectedModeControl.value
				),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(() => {
				this.selectedModeControl.setValue(clipboardManager.currentPasteMode);
			});

		// Show the clipboard on "Copy to..." operations:
		clipboardManager.copyTicketOperation$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((op) => this.planState.scopeSoup.floatingRegionApi.show());

		// Update warning icon next to the Exact Copy menu item:
		clipboardManager.copyTicketOperation$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((bool) => this.updateExactCopyMenuItem());

		// Update "clipboardHasPullTickets$":
		clipboardManager.copyTicketOperation$
			.pipe(
				map((op) => clipboardManager.ticketsArePullSide()),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.clipboardHasPullTickets$);

		this.clipboardHasPullTickets$.pipe(takeUntil(this.shutdownSubject$)).subscribe((hasPullTickets) => {
			hasPullTickets ? this.selectedModeControl.disable() : this.selectedModeControl.enable();
		});

		// Observable for plan loads:
		const planChanges = planState.stateChange$.pipe(
			filter((stateEvent: StateEvents) => stateEvent === StateEvents.FULLY_LOADED)
		);

		// Update "template" on "Copy to..." operations, plan changes and plan access changes:
		const threeEventSources$ = merge(
			clipboardManager.copyTicketOperation$,
			planChanges,
			planAccessCheck.hasRestrictedAccess$.pipe(
				tap((readOnly) => {
					this.readOnlyAccess = readOnly;
					clipboardManager.readOnlyAccess = readOnly;
				})
			)
		);

		threeEventSources$
			.pipe(
				tap((op) => this.updateClipboardLabel()),
				tap((op) => this.updateClipboardImage()),
				tap((op) => this.updateTicketCountString()),
				map((x) => this.showClearButton()),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.showClearButton$);

		// Use "planChanges to watch for changes to the active line location:
		planChanges
			.pipe(
				switchMap((event) => this.planState.plan.object$),
				filter((planData) => !!planData), // Only emit when we have plan data
				map((data) => ({
					activeLineX: data.view.activeLineX,
					activeLineDate: data.view.activeLineDate,
				})),
				distinctUntilChanged(
					(d1: any, d2: any) => d1.activeLineX === d2.activeLineX && d1.activeLineDate === d2.activeLineDate
				),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((data) => {
				clipboardManager.handleActiveLineChange();
				this.updateExactCopyMenuItem();
			});

		this.selectedModeControl.valueChanges
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((newMode: PasteMode) => {
				if (clipboardManager.currentPasteMode !== newMode) {
					clipboardManager.currentPasteMode = newMode;
					this.updateExactCopyMenuItem();
				}
			});
	}

	public ngAfterViewInit() {
		if (this.clipboardDragAndDropElement) {
			const mousePressFunc = (event: MouseEvent) => this.mousePress();
			const dragStartFunc = (event: MouseEvent) => this.dragStart(event);
			const dragFunc = (event: MouseEvent) => clipboardManager.drag(event, this.planState);
			const dragEndFunc = (event: MouseEvent) => clipboardManager.dragEnd(event, this.planState);

			/*
				Why the need for the "mousePressFunc" ?

				Reason 1:
				It’s very important to display the MoveActiveLinePopup dialog on the mouse press, and not on a click.
				A click’s event fires on the mouse release of a press/release combination. If the user doesn’t see the
				dialog until the mouse release, the user could easily: press, move the mouse around, thinking that’s
				needed for the paste’s drag-n-drop, not see any DnD preview (because it’s the “problematic Exact Copy paste condition”),
				and then think there’s a bug in Touchplan. Finally the user gives up in frustration and releases the mouse button,
				and only THEN does the dialog show up.  That’s a bad user experience.  That’s why it’s important to display the dialog
				on the mouse press.

				Reason 2:
				We added code so that the drag only "starts" after the user has mouse-pressed AND moved the mouse a certain number
				of pixels beyond a threshold count.  That's because just plain mouse clicks, and short press-move-release actions
				were being interpreted as drag-n-drops for a paste incorrectly.  A clear and long-enough drag was realized to be
				needed, so "dragStart" only is called after that move-threshold is reached.  But in the case where we need to
				display the dialog to ask the user about moving the Active line, that needs to happen immediately upon a mouse press.
			*/
			this.dragAndDropHandler = new DragAndDropHandler(
				this.clipboardDragAndDropElement,
				dragStartFunc,
				dragFunc,
				dragEndFunc,
				mousePressFunc
			);
		}
	}

	private mousePress(): AbortOrContinue {
		if (!this.pasteWithinProject()) {
			Logging.warning(strings.WARNING_CANNOT_PASTE_TICKET_FROM_DIFFERENT_PROJECT);
			return AbortOrContinue.Abort;
		}
		if (clipboardManager.showWarningIcon()) {
			this.displayActiveLineMovePrompt();
			return AbortOrContinue.Abort;
		}
		return AbortOrContinue.Continue;
	}

	private dragStart(event: MouseEvent): AbortOrContinue {
		return clipboardManager.dragStart(event, this.planState);
	}

	public handleClearButton() {
		clipboardManager.clearTickets();
	}

	public displayPasteModeInfoPopup() {
		const popupText: SimplePopupInputs = {};
		this.popupService.openAndHandleUpdate(
			popupText,
			(output) => {
				/* Don't particularly care when the user dismisses the dialog */
			},
			PasteModesInfoPopupComponent
		);
	}

	public displayActiveLineMovePrompt() {
		// Display the popup:
		this.popupService.openAndHandleUpdate(
			{},
			(output) => {
				if (output.type === PopupOutputType.update) {
					// Move the Active line:
					this.moveActiveLine(output.data as Date);
				}
			},
			MoveActiveLinePopupComponent
		);
	}

	private moveActiveLine(date: Date) {
		const dateString: string = dateCache.getFormat(date, 'YYYY-MM-DD');
		// "saveActiveLine" takes a string parameter
		this.planState.scopeSoup.saveActiveLine(dateString);
	}

	public pasteWithinProject(): boolean {
		return (
			!clipboardManager.ticketsProjectId() || clipboardManager.ticketsProjectId() === this.planState.projectId
		);
	}

	public updateClipboardImage() {
		if (this.readOnlyAccess || !this.pasteWithinProject()) {
			this.imageSource = ClipboardInDockComponent.CLIPBOARD_IMAGE_DISABLED;
		} else {
			this.imageSource =
				clipboardManager.ticketCount() > 0
					? ClipboardInDockComponent.CLIPBOARD_IMAGE_WITH_TICKETS
					: ClipboardInDockComponent.CLIPBOARD_IMAGE_EMPTY;
		}
	}

	public updateClipboardLabel() {
		if (this.readOnlyAccess || !this.pasteWithinProject() || clipboardManager.ticketCount() === 0) {
			this.clipboardLabel = strings.CLIPBOARD_LABEL;
		} else {
			this.clipboardLabel = strings.DRAG_TO_PASTE_LABEL;
		}
	}

	public updateTicketCountString() {
		this.ticketCountString = this.pasteWithinProject() ? clipboardManager.ticketCountString() : '';
	}

	public showClearButton(): boolean {
		return !this.pasteWithinProject() || clipboardManager.ticketCountString() !== '';
	}

	private showExactCopyMenuItemWarning(mode: PasteMode): boolean {
		if (clipboardManager.ticketCount() === 0 || clipboardManager.ticketsArePullSide()) {
			return false;
		}
		// So here we know the clipboard isn't empty, has active tickets, and the paste-mode is Paste-All:
		return !clipboardManager.canExactCopyTickets(mode);
	}

	private updateExactCopyMenuItem() {
		for (const option of this.dropdownOptions) {
			if (option.value === PasteMode.ExactCopy) {
				option.icon = this.showExactCopyMenuItemWarning(PasteMode.ExactCopy) ? 'warning' : '';
			}
		}
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		if (this.dragAndDropHandler) {
			this.dragAndDropHandler.destroy();
			this.dragAndDropHandler = null;
		}
	}
}
